import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import { getAuth, Auth } from "firebase/auth";
import { getAnalytics, Analytics, isSupported } from "firebase/analytics";
import { getMessaging, getToken, isSupported as isSupportedMsg, Messaging } from "firebase/messaging";

import firebaseConfig from "../config/firebaseConfig";
import getEnv from "~/utils/get-env";
import { setCookie } from "~/utils/cookies";
import { FCM_TOKEN } from "~/utils/constant";
import type { AppDispatch } from "../store/store";
import { FcmTokenData } from "../store/slice/settingSlice";

let app: FirebaseApp;
let auth: Auth;
let analytics: Analytics | null = null;

const env = getEnv();

/**
 * Initialize Firebase App (runs only in browser)
 */
function initApp(): FirebaseApp {
  if (typeof window === "undefined") {
    throw new Error("Firebase cannot run on server");
  }

  if (!getApps().length) {
    app = initializeApp(firebaseConfig);
  } else {
    app = getApps()[0];
  }

  return app;
}

/**
 * Get Firebase Auth (sync)
 */
export function getFirebaseAuth(): Auth {
  if (!auth) {
    const appInstance = initApp();
    auth = getAuth(appInstance);
  }
  return auth;
}

/**
 * Get Firebase Analytics (async)
 */
export async function getFirebaseAnalytics(): Promise<Analytics | null> {
  if (typeof window === "undefined") return null;

  if (analytics) return analytics;

  const supported = await isSupported();
  if (!supported) return null;

  const appInstance = initApp();
  analytics = getAnalytics(appInstance);

  return analytics;
}

/**
 * Get device type based on platform/OS
 * @returns "Android" | "iOS" | "Web"
 */
export function getDeviceType(): string {
  if (typeof window === "undefined") return "Web";

  const userAgent = navigator.userAgent;

  // Check for iOS devices (iPhone, iPad, iPod)
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return "iOS";
  }

  // Check for Android devices
  if (/Android/i.test(userAgent)) {
    return "Android";
  }

  // Everything else is considered Web (desktop browsers, etc.)
  return "Web";
}

/**
 * Get user's geolocation information (IP, country code, country)
 * @returns Promise with ip_address, country_code, and country
 */
export async function getGeoLocation(): Promise<{
  ip_address: string;
  country_code: string;
  country: string;
}> {
  try {
    // Using ipapi.co free API (no API key required)
    const response = await fetch("https://ipapi.co/json/");

    if (!response.ok) {
      throw new Error("Failed to fetch geolocation");
    }

    const data = await response.json();

    return {
      ip_address: data.ip || "",
      country_code: data.country_code || "",
      country: data.country_name || "",
    };
  } catch (error) {
    console.error("Error fetching geolocation:", error);
    // Return empty values as fallback
    return {
      ip_address: "",
      country_code: "",
      country: "",
    };
  }
}

/**
 * Get Firebase Messaging (async)
 * @param dispatch - Optional Redux dispatch function to automatically insert FCM token
 */
export async function getFirebaseMessaging(dispatch?: AppDispatch): Promise<Messaging | null> {
  if (typeof window === "undefined") return null;

  // Firebase Messaging requires HTTPS (or localhost). On plain IP access or
  // HTTP the browser won't expose ServiceWorker/PushManager and Firebase
  // throws "messaging/unsupported-browser".
  if (!window.isSecureContext) {
    console.warn("Firebase Messaging requires a secure context (HTTPS or localhost). Push notifications are disabled.");
    return null;
  }

  const supported = await isSupportedMsg();
  if (!supported) {
    console.warn("Firebase Messaging is not supported in this browser.");
    return null;
  }

  const permission = await Notification.requestPermission();

  const appInstance = initApp();
  const messaging = getMessaging(appInstance);

  const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  const token = await getToken(messaging, {
    vapidKey: env.VAPID_KEY,
    serviceWorkerRegistration: registration,
  });

  if (token) {
    console.log("FCM Token:", token);
    setCookie(FCM_TOKEN, token, {
      path: "/", // cookie available across the site
      expires: 365 * 20, // cookie expiry (in seconds) - 24 hours
      secure: false, // only over HTTPS
      sameSite: "lax", // CSRF protection
    });

    // Dispatch the insertFcmTokenThunk if dispatch is provided
    if (dispatch) {
      const { insertFcmTokenThunk } = await import("../store/slice/settingSlice");
      const permissionGranted = permission === "granted" ? 1 : 0;

      // Fetch geolocation data
      const geoData = await getGeoLocation();

      const payload: FcmTokenData = {
        fcm_token: token as string,
        daily_darshan: permissionGranted,
        daily_katha: permissionGranted,
        new_releases: permissionGranted,
        live_stream: permissionGranted,
        all_other: permissionGranted,
        device_type: getDeviceType(),
        device_browser: navigator.userAgent,
        os_type: navigator.platform,
        os_browser: navigator.userAgent,
        ip_address: geoData.ip_address,
        country_code: geoData.country_code,
        country: geoData.country,
      };

      console.log("Dispatching insertFcmTokenThunk with payload:", payload);

      dispatch(insertFcmTokenThunk(payload));
    }
  }

  return messaging;
}
